/**
 * Auth Provider
 * 
 * จัดการ Authentication State ทั้งแอป
 * - ตรวจสอบ session ตอนโหลดแอป
 * - Auto-logout เมื่อ session หมดอายุ
 * - Provide auth context ให้ components
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserService } from '@/domain/user';
import type { AdminUser } from '@/domain/user';
import { getStoredSessionToken, getStoredUser, clearStoredSession } from '@/features/auth/admin-login';

// ============================================
// Types
// ============================================

interface AuthContextType {
  user: AdminUser | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ============================================
// Context
// ============================================

const AuthContext = createContext<AuthContextType | null>(null);

// ============================================
// Hook
// ============================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// ============================================
// Provider
// ============================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  
  const [user, setUser] = useState<AdminUser | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const isAuthenticated = !!sessionToken && !!user;
  
  // ============================================
  // Logout
  // ============================================
  
  const logout = useCallback(async () => {
    console.log('🚪 Logging out...');
    
    // ⭐ เก็บข้อมูล user ก่อน logout เพื่อส่งไปบันทึก Audit Log
    const currentUser = user;
    
    try {
      if (sessionToken) {
        const service = await getUserService();
        // ส่ง userData ไปด้วยเพื่อให้ backend สร้าง audit log
        await service.logout(sessionToken, currentUser || undefined);
      }
    } catch (error) {
      console.warn('Logout API failed:', error);
    }
    
    // Clear local state
    setUser(null);
    setSessionToken(null);
    clearStoredSession();
    
    // Redirect to login
    navigate('/login', { replace: true });
  }, [sessionToken, user, navigate]);
  
  // ============================================
  // Refresh User
  // ============================================
  
  const refreshUser = useCallback(async () => {
    const token = getStoredSessionToken();
    if (!token) return;
    
    try {
      const service = await getUserService();
      const result = await service.validateSession(token);
      
      if (result.valid && result.user) {
        setUser(result.user);
        setSessionToken(token);
      } else {
        // Session invalid
        await logout();
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  }, [logout]);
  
  // ============================================
  // Initialize Auth on Mount
  // ============================================
  
  useEffect(() => {
    async function initAuth() {
      console.log('🔐 Initializing auth...');
      setIsLoading(true);
      
      const token = getStoredSessionToken();
      const storedUser = getStoredUser();
      
      // No stored session
      if (!token) {
        console.log('📭 No session found');
        setIsLoading(false);
        return;
      }
      
      // Have stored session - validate it
      console.log('🔍 Validating session...');
      
      try {
        const service = await getUserService();
        const result = await service.validateSession(token);
        
        if (result.valid && result.user) {
          console.log('✅ Session valid:', result.user.username);
          setUser(result.user);
          setSessionToken(token);
        } else {
          console.log('❌ Session invalid:', result.error);
          clearStoredSession();
          setUser(null);
          setSessionToken(null);
        }
      } catch (error: any) {
        console.error('Session validation error:', error);
        
        // Check if it's a network error (offline mode)
        const isNetworkError = error?.message?.includes('fetch') || 
                               error?.message?.includes('network') ||
                               error?.name === 'TypeError';
        
        if (isNetworkError && storedUser) {
          // Only use fallback for genuine network issues
          console.log('⚠️ Network error - using stored user (offline mode)');
          setUser(storedUser);
          setSessionToken(token);
        } else {
          // For all other errors, clear session (including invalid session errors)
          console.log('🚫 Session error - clearing session');
          clearStoredSession();
          setUser(null);
          setSessionToken(null);
        }
      }
      
      setIsLoading(false);
    }
    
    initAuth();
  }, []);
  
  // ============================================
  // Listen for storage changes (multi-tab support)
  // ============================================
  
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === 'admin_session_token') {
        if (!e.newValue) {
          // Logged out in another tab
          setUser(null);
          setSessionToken(null);
          navigate('/login', { replace: true });
        } else if (e.newValue !== sessionToken) {
          // Logged in in another tab
          refreshUser();
        }
      }
    }
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [sessionToken, navigate, refreshUser]);
  
  // ============================================
  // Render
  // ============================================
  
  const value: AuthContextType = {
    user,
    sessionToken,
    isAuthenticated,
    isLoading,
    logout,
    refreshUser,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

